import styled from '@emotion/styled';
import { Flex, Heading } from '@qga/roo-ui/components';
import { themeGet } from 'styled-system';

import type { PackageOption as PackageOptionType } from '@shared/v2/utils/getPackageOptions';
import { BEST_RATE_TEXT } from '@shared/v2/constants/packages';

import useProcuredOffer from '@/components/PackagePage/hooks/useProcuredOffer';
import Text from '@/components/Text';
import usePropertyContent from '@/hooks/usePropertyContent';
import usePrependFlightInclusion from '@/v2/components/pages/PackageViewV2/hooks/usePrependFlightInclusion';
import PackageDetails from '@/v2/components/ui/PackageDetails';
import { PackageInclusions } from '@/v2/components/ui/PackageInclusions';
import { UI } from '@/v2/types';
import { convertLeadInOfferInclusion } from '@/v2/utils/inclusions';

const StyledHeading = styled(Heading.h4)`
  margin: 0;
  font-family: ${themeGet('headerFontFamily')};
`;

const ColumnFlex = styled(Flex)`
  flex-direction: column;
`;

export type PackageOptionDetailsProps = {
  selectedPackageOption: PackageOptionType;
  limitInclusions?: boolean;
};

const PackageOptionDetails = ({
  selectedPackageOption,
  limitInclusions = false,
}: PackageOptionDetailsProps) => {
  const propertyContent: UI.Property = usePropertyContent();
  const procuredOffer: UI.ProcuredOffer | undefined = useProcuredOffer();
  const baseInclusions =
    selectedPackageOption?.cheapestOffer?.leadInOffer?.inclusions.map(
      convertLeadInOfferInclusion,
    );
  const inclusions = usePrependFlightInclusion(baseInclusions);

  const inclusionLimit = limitInclusions ? 4 : Infinity;

  return (
    <ColumnFlex gap="24px" data-testid="PACKAGE_OPTION_ROOM_OPTION_DETAILS">
      <ColumnFlex alignItems="flex-start" gap="16px">
        <ColumnFlex gap="4px">
          <Text
            bold
            fontSize={['14px', null, '16px']}
            lineHeight={['20px', null, '24px']}
            letterSpacing="0.1px"
          >
            Your package option
          </Text>
          <StyledHeading
            fontSize={['18px', null, '24px']}
            lineHeight={['24px', null, '30px']}
          >
            {selectedPackageOption?.title ?? BEST_RATE_TEXT}
          </StyledHeading>
        </ColumnFlex>
        <ColumnFlex gap="8px" alignItems="flex-start">
          <PackageInclusions
            inclusions={inclusions ?? []}
            numberToShow={inclusionLimit}
          />
          <PackageDetails
            propertyContent={propertyContent}
            procuredOffer={procuredOffer}
            leadInOffer={selectedPackageOption?.cheapestOffer?.leadInOffer}
            inclusions={inclusions ?? []}
          />
        </ColumnFlex>
      </ColumnFlex>
    </ColumnFlex>
  );
};

export default PackageOptionDetails;
